import React, { useEffect, useRef, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { AlertCircle, Loader2, ShieldCheck } from 'lucide-react';

import {
  createMicrosoftSSOCorrelation,
  identityAPI,
} from '../services/api';

export default function MicrosoftIdentitySetupStartPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const setupPromiseRef = useRef(null);

  const [error, setError] = useState('');
  const org = searchParams.get('org') || '';
  const uidb64 = searchParams.get('uidb64') || '';
  const token = searchParams.get('token') || '';

  useEffect(() => {
    let active = true;

    if (!setupPromiseRef.current) {
      setupPromiseRef.current = (async () => {
        if (!org || !uidb64 || !token) {
          throw new Error('Microsoft setup link is missing required parameters.');
        }

        const browserCorrelation = createMicrosoftSSOCorrelation();
        const { data } = await identityAPI.startMicrosoftBootstrap({
          org,
          uidb64,
          token,
          browser_correlation: browserCorrelation,
        });

        if (!data.authorization_url) {
          throw new Error('Microsoft authorization URL was not returned.');
        }

        return data.authorization_url;
      })();
    }

    setupPromiseRef.current
      .then((authorizationUrl) => {
        if (active) {
          window.location.assign(authorizationUrl);
        }
      })
      .catch((err) => {
        if (!active) return;
        setError(
          err?.response?.data?.detail ||
            err?.response?.data?.error ||
            err?.message ||
            'Unable to start Microsoft setup.',
        );
      });

    return () => {
      active = false;
    };
  }, [org, token, uidb64]);

  return (
    <div className="flex min-h-screen items-center justify-center bg-slate-50 px-4">
      <div className="w-full max-w-md rounded-3xl border border-slate-200 bg-white p-6 text-center shadow-sm">
        {error ? (
          <>
            <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-2xl bg-red-50 text-red-600">
              <AlertCircle className="h-6 w-6" />
            </div>

            <h1 className="mt-4 text-xl font-black text-slate-950">
              Microsoft setup could not start
            </h1>

            <p className="mt-2 text-sm leading-6 text-slate-600">{error}</p>

            <button
              type="button"
              onClick={() => navigate('/login')}
              className="mt-6 rounded-xl bg-orange-600 px-4 py-2.5 text-sm font-bold text-white hover:bg-orange-700"
            >
              Back to login
            </button>
          </>
        ) : (
          <>
            <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-2xl bg-orange-50 text-orange-600">
              <ShieldCheck className="h-6 w-6" />
            </div>

            <h1 className="mt-4 text-xl font-black text-slate-950">
              Starting Microsoft setup
            </h1>

            <p className="mt-2 text-sm leading-6 text-slate-600">
              Redirecting you to Microsoft to connect your organization tenant.
            </p>

            <div className="mt-6 flex items-center justify-center gap-2 text-sm font-semibold text-slate-500">
              <Loader2 className="h-4 w-4 animate-spin text-orange-600" />
              Preparing secure redirect
            </div>
          </>
        )}
      </div>
    </div>
  );
}
